import React, { useState, useEffect, useRef } from 'react';
import { firestoreService } from '../../services/firestoreService';
import { requestsService } from '../../services/requestsService';

const ProviderProfile = ({ bookId, userId, onClose }) => {
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);

  const [businessName, setBusinessName] = useState('');
  const [category, setCategory] = useState('');

  const [aboutText, setAboutText] = useState('');
  const [services, setServices] = useState([]);

  const [portfolioImages, setPortfolioImages] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [reviews] = useState([]); // future integration
  const [visibleReviews, setVisibleReviews] = useState(3);

  // ------------------------------------------------------------
  // Load provider profile
  // ------------------------------------------------------------
  useEffect(() => {
    const load = async () => {
      const prof = await requestsService.getProviderProfile(bookId, userId);
      if (prof) {
        setProfile(prof);
        setBusinessName(prof.businessName || '');
        setCategory(prof.category || '');
        setAboutText(prof.aboutText || '');
        setServices(prof.services || []);
        setPortfolioImages(prof.portfolioImages || []);
      }
    };
    load();
  }, [bookId, userId]);

  // ------------------------------------------------------------
  // Save profile
  // ------------------------------------------------------------
  const saveProfile = async () => {
    const updated = {
      businessName,
      category,
      aboutText,
      services,
      portfolioImages,
    };

    if (profile?.id) {
      await requestsService.updateProviderProfile(bookId, profile.id, updated);
    } else {
      const newId = await requestsService.createProviderProfile(
        bookId,
        userId,
        updated
      );
      profile.id = newId;
    }

    setProfile(prev => ({ ...prev, ...updated }));
    setEditing(false);
  };

  // ------------------------------------------------------------
  // Upload portfolio images (using firestoreService)
  // ------------------------------------------------------------
  const handleUploadClick = () => fileInputRef.current?.click();

  const handleFileChange = async e => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);

    const uploadedUrls = [];

    for (const file of Array.from(files)) {
      try {
        const url = await firestoreService.uploadFile(
          file,
          `pb-books/${bookId}/providers/${userId}/portfolio`,
          bookId
        );
        uploadedUrls.push(typeof url === 'string' ? url : url.url);
      } catch (err) {
        console.error('Upload failed:', err);
      }
    }

    setPortfolioImages(prev => [...prev, ...uploadedUrls]);
    setIsUploading(false);
  };

  if (!profile && !editing)
    return (
      <div className="p-6 text-center text-slate-500">
        Loading provider profile…
      </div>
    );

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header / basic info */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
        <div className="h-40 bg-gradient-to-r from-indigo-600 to-purple-600 relative">
          <button
            className="absolute top-4 right-4 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white px-4 py-2 rounded-lg"
            onClick={() => (editing ? saveProfile() : setEditing(true))}
          >
            {editing ? 'Save' : 'Edit'}
          </button>
        </div>

        <div className="px-8 pb-8 -mt-12">
          {/* Avatar + headline */}
          <div className="flex items-end gap-6 mb-6">
            <div className="w-28 h-28 rounded-2xl bg-white dark:bg-slate-900 shadow-lg flex items-center justify-center text-3xl font-bold text-slate-500">
              {(businessName || '?').substring(0, 2)}
            </div>

            <div className="flex-1">
              {editing ? (
                <input
                  type="text"
                  className="w-full text-3xl font-bold bg-slate-100 dark:bg-slate-700 p-2 rounded-lg"
                  value={businessName}
                  onChange={e => setBusinessName(e.target.value)}
                />
              ) : (
                <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
                  {businessName}
                </h1>
              )}

              {editing ? (
                <input
                  type="text"
                  className="mt-1 w-full bg-slate-100 dark:bg-slate-700 p-2 rounded-lg"
                  value={category}
                  onChange={e => setCategory(e.target.value)}
                />
              ) : (
                <p className="text-slate-500 dark:text-slate-400 font-medium">
                  {category}
                </p>
              )}
            </div>
          </div>

          {/* About */}
          <section className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <h3 className="font-bold text-lg text-slate-900 dark:text-white">
                About
              </h3>
            </div>

            {editing ? (
              <textarea
                className="w-full p-4 bg-slate-100 dark:bg-slate-700 rounded-xl"
                rows={4}
                value={aboutText}
                onChange={e => setAboutText(e.target.value)}
              />
            ) : (
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                {aboutText}
              </p>
            )}
          </section>

          {/* Portfolio */}
          <section className="mb-8">
            <h3 className="font-bold text-lg text-slate-900 dark:text-white mb-3">
              Portfolio
            </h3>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {portfolioImages.map((img, i) => (
                <div
                  key={i}
                  className="aspect-square rounded-xl overflow-hidden bg-slate-100 dark:bg-slate-700"
                >
                  <img
                    src={img}
                    className="w-full h-full object-cover"
                    alt="Portfolio"
                  />
                </div>
              ))}

              {editing && (
                <>
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    accept="image/*"
                    multiple
                    onChange={handleFileChange}
                  />

                  <button
                    onClick={handleUploadClick}
                    disabled={isUploading}
                    className="aspect-square flex items-center justify-center border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-xl text-slate-500 hover:text-indigo-600"
                  >
                    {isUploading ? 'Uploading…' : '+ Add'}
                  </button>
                </>
              )}
            </div>
          </section>

          {/* Services */}
          <section className="mb-8">
            <h3 className="font-bold text-lg text-slate-900 dark:text-white mb-3">
              Services
            </h3>

            {editing ? (
              <textarea
                className="w-full p-3 bg-slate-100 dark:bg-slate-700 rounded-xl"
                rows={3}
                placeholder="Comma-separated, e.g. Design, Installation…"
                value={services.join(', ')}
                onChange={e =>
                  setServices(
                    e.target.value
                      .split(',')
                      .map(s => s.trim())
                      .filter(Boolean)
                  )
                }
              />
            ) : (
              <div className="flex flex-wrap gap-2">
                {services.map((s, i) => (
                  <span
                    key={i}
                    className="px-3 py-1 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg text-xs"
                  >
                    {s}
                  </span>
                ))}
              </div>
            )}
          </section>

          {/* Reviews (future feature) */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-lg text-slate-900 dark:text-white">
                Reviews ({reviews.length})
              </h3>
            </div>

            {reviews.slice(0, visibleReviews).map(r => (
              <div
                key={r.id}
                className="p-4 bg-slate-50 dark:bg-slate-900/50 rounded-xl mb-4"
              >
                <p className="font-semibold text-slate-900 dark:text-white">
                  {r.author}
                </p>
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  {r.text}
                </p>
              </div>
            ))}

            {visibleReviews < reviews.length && (
              <button
                onClick={() => setVisibleReviews(v => v + 3)}
                className="px-4 py-2 bg-slate-200 dark:bg-slate-700 rounded-lg text-sm"
              >
                Load More
              </button>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default ProviderProfile;
